import React from 'react';
import {TreeItem} from '@mui/lab';
import {Typography} from '@mui/material';

export const removeL3Duplicates = (array) => {
    const seen = new Set();
    return array.filter((item) => {
        if (seen.has(item.L3)) {
            return false;
        }
        seen.add(item.L3);
        return true;
    });
};

export const getLabelForCode = (code, allL3Cats) => {
    const match = allL3Cats.find((item) => item.L3.startsWith(`${code} -`));
    return match ? match.L3 : code;
};

export const buildSuggestionTree = (parsedML, allL3Cats) =>
    Object.entries(parsedML.Level1 || {}).map(([level1Key, level1Value]) => {
        const level1Label = getLabelForCode(level1Key, allL3Cats);
        const level1Percent = level1Value.Value ? ` (${parseFloat(level1Value.Value.replace('%', '')).toFixed(2)}%)` : '';
        const level2Nodes = Object.entries(level1Value.Level2 || {}).map(([level2Key, level2Value]) => {
            const level2Label = getLabelForCode(level2Key, allL3Cats);
            const level2Percent = level2Value.Value ? ` (${parseFloat(level2Value.Value.replace('%', '')).toFixed(2)}%)` : '';
            const level3Nodes = level2Value.Level3 ?
                Object.entries(level2Value.Level3).map(([level3Key, percent]) => {
                    const level3Label = getLabelForCode(level3Key, allL3Cats);
                    const level3Percent = percent ? ` (${parseFloat(percent.replace('%', '')).toFixed(2)}%)` : '';
                    return {
                        id: level3Key,
                        label: level3Label + level3Percent,
                        value: level3Label,
                    };
                }) :
                [];
            return {
                id: level2Key,
                label: level2Label + level2Percent,
                children: level3Nodes,
            };
        });
        return {
            id: level1Key,
            label: level1Label + level1Percent,
            children: level2Nodes,
        };
    });

export const buildAllTree = (items) => {
    const filtered = items.filter((item) =>
        item.L3?.startsWith('S'),
    );

    const nodesByCode = new Map();

    filtered.forEach((item) => {
        const code = item.L3.split(' - ')[0];

        nodesByCode.set(code, {
            id: code,
            label: item.L3,
            value: item.level === 'SIM_LEVEL3' ? item.L3 : undefined,
            level: item.level,
            parent: item.parent || '',
            children: [],
        });
    });

    const roots = [];

    nodesByCode.forEach((node) => {
        if (!node.parent) {
            roots.push(node);
            return;
        }

        const parentNode = nodesByCode.get(node.parent);
        if (parentNode) {
            parentNode.children.push(node);
        } else {
            console.warn(
                `[L3 TREE] Orphan node after filtering: ${node.id} (parent ${node.parent})`,
            );
        }
    });

    return roots;
};

export const flattenTree = (nodes) => {
    let result = [];
    for (const node of nodes) {
        result.push(node);
        if (node.children) {
            result = result.concat(flattenTree(node.children));
        }
    }
    return result;
};

export const filterOptions = (options, state) => {
    const {inputValue} = state;
    const lowerInput = inputValue.toLowerCase();
    return options.filter((item) => item.label.toLowerCase().includes(lowerInput));
};

export const isDescendantSelected = (node, selectedValue) => {
    if (selectedValue?.value === node.value) {
        return true;
    }

    if (!node.children || node.children.length === 0) {
        return false;
    }

    return node.children.some((child) => isDescendantSelected(child, selectedValue));
};

export const findPath = (node, selectedValue) => {
    if (node.value === selectedValue?.value) {
        return [node.id];
    }

    if (!node.children) {
        return [];
    }

    for (const child of node.children) {
        const childPath = findPath(child, selectedValue);
        if (childPath.length > 0) {
            return [node.id, ...childPath];
        }
    }
    return [];
};


export const getSelectedPath = (nodes, selectedValue) => {
    if (!selectedValue) {
        return new Set();
    }
    for (const rootNode of nodes) {
        const path = findPath(rootNode, selectedValue);
        if (path.length > 0) {
            return new Set(path);
        }
    }
    return new Set();
};

export const renderTreeNodes = (nodes, pathIds, onSelect) => {
    return nodes.map((node) => {
        const isHighlighted = pathIds.has(node.id);
        return (
            <TreeItem
                key={node.id}
                nodeId={node.id}
                sx={{
                    '& .MuiTreeItem-content': {
                        'width': '100%',
                    },
                }}
                label={
                    <Typography
                        variant="body1"
                        sx={{
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontWeight: isHighlighted ? 'bold' : 'normal',
                        }}
                        onClick={(e) => {
                            if (!node.children || node.children.length === 0) {
                                e.stopPropagation();
                                onSelect(node);
                            }
                        }}
                    >
                        {node.label}
                    </Typography>
                }
            >
                {node.children && node.children.length > 0 ?
                    renderTreeNodes(node.children, pathIds, onSelect) :
                    null}
            </TreeItem>
        );
    });
};


export const pruneTree = (nodes, filter) => {
    const lower = filter.toLowerCase();
    return nodes.reduce((acc, node) => {
        const matches = node.label.toLowerCase().includes(lower);
        let prunedChildren = [];
        if (node.children) {
            prunedChildren = pruneTree(node.children, filter);
        }
        if (matches || prunedChildren.length) {
            acc.push({
                ...node,
                children: prunedChildren,
            });
        }
        return acc;
    }, []);
};
